import * as React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { Modal, Button, Alert, Table, Badge, Spinner, Form } from 'react-bootstrap';
import { LuFiles, LuMerge, LuCheckCircle, LuLink } from 'react-icons/lu';
import * as models from '../../models'; 
import { contactsActions } from 'store/contacts';

interface Props extends PropsFromRedux {
    show: boolean;
    onHide: () => void;
    currentContact: models.Contact; 
}

interface State {
    selectedIds: number[];
    showSuccess: boolean;
}

class MergeModal extends React.Component<Props, State> {
    
    state: State = {
        selectedIds: [],
        showSuccess: false
    };

    componentDidUpdate(prevProps: Props) {
        if (this.props.show && !prevProps.show) {
            this.setState({ showSuccess: false, selectedIds: [] });
            
            if (this.props.currentContact?.contactId) {
                this.props.getDupes(this.props.currentContact.contactId);
            }
        }
        
        if (prevProps.contactDupes && this.props.contactDupes) {
             if (prevProps.contactDupes.resourceList?.length !== this.props.contactDupes.resourceList?.length) {
                 this.setState({ selectedIds: [] }); 
                 if (this.props.contactDupes.resourceList?.length === 0) {
                     this.setState({ showSuccess: true });
                 }
             }
        }
    }

    handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { contactDupes } = this.props;
        const allList = contactDupes?.resourceList || [];
        
        if (e.target.checked) {
            this.setState({ selectedIds: allList.map(d => d.contactId) });
        } else {
            this.setState({ selectedIds: [] });
        }
    }

    handleSelectRow = (contactId: number) => {
        const { selectedIds } = this.state;
        if (selectedIds.includes(contactId)) {
            this.setState({ selectedIds: selectedIds.filter(id => id !== contactId) });
        } else {
            this.setState({ selectedIds: [...selectedIds, contactId] });
        }
    }

    handleMerge = () => {
        const { currentContact } = this.props;
        const { selectedIds } = this.state;

        if (!currentContact || selectedIds.length === 0) return;
        this.props.mergeContacts(currentContact.contactId, selectedIds);
    }

    render() {
        const { show, onHide, currentContact, isContactDupesLoading, contactDupes, isContactMerging } = this.props;
        const { selectedIds, showSuccess } = this.state;

        const duplicates = contactDupes?.resourceList || [];
        const totalCount = contactDupes?.totalCount || 0;
        const isLoading = isContactDupesLoading;

        const allSelected = duplicates.length > 0 && selectedIds.length === duplicates.length;
        const indeterminate = selectedIds.length > 0 && selectedIds.length < duplicates.length;

        return (
            <Modal show={show} onHide={onHide} size="xl" backdrop="static">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center">
                        <LuFiles className="me-2 text-primary" />
                        Find Duplicates 
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0"> 
                    
                    {/* --- CONTEXT HEADER --- */}
                    <div className="p-3">
                        <Alert variant="primary" className="mb-0 shadow-sm border-primary">
                            <div className="d-flex align-items-start">
                                <LuMerge className="me-3 fs-3 mt-1" />
                                <div className="flex-grow-1">
                                    <div className="text-uppercase small fw-bold mb-1" style={{opacity: 0.8}}>Merging into Current Record</div>
                                    <div className="d-flex justify-content-between align-items-end">
                                        
                                        {/* LEFT: Name & Email */}
                                        <div>
                                            <span className="fs-5 fw-bold font-monospace">
                                                {currentContact.firstName} {currentContact.lastName} 
                                            </span>
                                            {currentContact.email && <span className="text-muted ms-2">({currentContact.email})</span>}
                                        </div>

                                        {/* RIGHT: Stats & IDs */}
                                        <div className="d-flex flex-column align-items-end gap-2">
                                            
                                            {/* Top: Connect IDs */}
                                            <div className="d-flex gap-2">
                                                <Badge 
                                                    bg={currentContact.connectAdjusterId ? "info" : "light"} 
                                                    text={currentContact.connectAdjusterId ? "dark" : "muted"}
                                                    className="fw-normal border d-flex align-items-center"
                                                >
                                                    <LuLink className="me-1" size={11}/> 
                                                    Adj: {currentContact.connectAdjusterId || <span className="fst-italic opacity-50 ms-1">None</span>}
                                                </Badge>

                                                <Badge 
                                                    bg={currentContact.connectAdminId ? "dark" : "light"}
                                                    text={currentContact.connectAdminId ? "white" : "muted"}
                                                    className="fw-normal border d-flex align-items-center"
                                                >
                                                    <LuLink className="me-1" size={11}/> 
                                                    Adm: {currentContact.connectAdminId || <span className="fst-italic opacity-50 ms-1">None</span>}
                                                </Badge>
                                            </div>

                                            {/* Bottom: Claim Counts */}
                                            <div className="d-flex gap-2">
                                                <Badge bg="light" text="dark" className="border d-flex align-items-center px-3">
                                                    <span className="fw-bold me-1">{currentContact.totalClaims || 0}</span> 
                                                    <span className="fw-normal text-muted">Total Claims</span>
                                                </Badge>
                                                <Badge bg="primary" className="d-flex align-items-center px-3 shadow-sm">
                                                    <span className="fw-bold me-1">{currentContact.activeClaims || 0}</span>
                                                    <span className="fw-normal">Active</span>
                                                </Badge>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </Alert>

                        {/* Success Message */}
                        {showSuccess && (
                            <Alert variant="success" className="mt-3 mb-0" onClose={() => this.setState({showSuccess: false})} dismissible>
                                <LuCheckCircle className="me-2 inline-block"/>
                                <strong>Success!</strong> The records have been merged successfully.
                            </Alert>
                        )}
                    </div>

                    {/* --- TABLE CONTENT --- */}
                    {isLoading ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" role="status" />
                            <p className="mt-3 text-muted">Scanning database for potential matches...</p>
                        </div>
                    ) : (
                        <>
                            {duplicates.length === 0 && !showSuccess ? (
                                <div className="text-center p-5 text-muted">
                                    <LuCheckCircle className="mb-3 text-success" size={48} />
                                    <h5>No Duplicates Found</h5>
                                    <p>This record appears to be unique.</p>
                                </div>
                            ) : (
                                <>
                                    {/* --- ACTION BAR (Above Table) --- */}
                                    <div className="px-3 py-2 bg-light border-top border-bottom d-flex justify-content-between align-items-center sticky-top">
                                        <div className="text-muted fw-bold small">
                                            Found {totalCount} potential matches
                                            {selectedIds.length > 0 && <span className="ms-2 text-primary">({selectedIds.length} selected)</span>}
                                        </div>
                                        <div>
                                            <Button 
                                                variant="primary" 
                                                size="sm"
                                                disabled={selectedIds.length === 0 || isContactMerging}
                                                onClick={this.handleMerge}
                                                className="d-flex align-items-center shadow-sm"
                                            >
                                                {isContactMerging ? (
                                                    <>
                                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>
                                                        Merging...
                                                    </>
                                                ) : (
                                                    <>
                                                        <LuMerge className="me-2"/> 
                                                        Merge Selected
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                    
                                    <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                        <Table striped hover responsive className="align-middle small mb-0">
                                            <thead className="bg-light sticky-top shadow-sm" style={{top: 0, zIndex: 1}}>
                                                <tr>
                                                    <th style={{width: '40px'}} className="text-center bg-light">
                                                        <Form.Check 
                                                            type="checkbox" 
                                                            checked={allSelected}
                                                            ref={(input: HTMLInputElement | null) => { if (input) input.indeterminate = indeterminate; }}
                                                            onChange={this.handleSelectAll}
                                                            disabled={isContactMerging} 
                                                        />
                                                    </th>
                                                    <th style={{width: '8%'}} className="bg-light">Score</th>
                                                    <th style={{width: '8%'}} className="bg-light">Status</th> 
                                                    <th style={{width: '20%'}} className="bg-light">Name / ID</th>
                                                    <th style={{width: '12%'}} className="bg-light">Claims</th> 
                                                    <th style={{width: '12%'}} className="bg-light">Connect IDs</th> 
                                                    <th style={{width: '20%'}} className="bg-light">Email / Phone</th>
                                                    <th style={{width: '20%'}} className="bg-light">Address</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {duplicates.map(dup => {
                                                    const isHighMatch = dup.matchScore >= 90;
                                                    const isSelected = selectedIds.includes(dup.contactId);
                                                    
                                                    return (
                                                        <tr 
                                                            key={dup.contactId} 
                                                            className={isSelected ? "table-active" : ""} 
                                                            style={{cursor: 'pointer'}}
                                                            onClick={() => !isContactMerging && this.handleSelectRow(dup.contactId)}
                                                        >
                                                            <td className="text-center">
                                                                <Form.Check 
                                                                    type="checkbox" 
                                                                    checked={isSelected}
                                                                    onChange={() => {}}
                                                                    disabled={isContactMerging}
                                                                    onClick={(e) => e.stopPropagation()} 
                                                                    onChangeCapture={() => this.handleSelectRow(dup.contactId)}
                                                                />
                                                            </td>
                                                            
                                                            <td>
                                                                <Badge bg={isHighMatch ? 'success' : dup.matchScore > 70 ? 'warning' : 'secondary'} text={!isHighMatch && dup.matchScore > 70 ? 'dark' : 'white'}>
                                                                    {dup.matchScore}%
                                                                </Badge>
                                                                <div className="text-muted mt-1" style={{fontSize: '0.7rem'}}>{dup.matchReason}</div>
                                                            </td>

                                                            <td>
                                                                {dup.isActive ? <Badge bg="success-subtle" text="success" className="border border-success">Active</Badge> 
                                                                              : <Badge bg="secondary" className="fw-normal">Inactive</Badge>}
                                                            </td>

                                                            <td>
                                                                <div className="fw-bold text-dark">{dup.firstName} {dup.lastName}</div>
                                                                <div className="text-muted">ID: {dup.contactId}</div>
                                                            </td>

                                                            <td>
                                                                <div className="fw-bold">{dup.claimCount} Total</div>
                                                                {dup.activeClaims > 0 ? <span className="text-primary fw-bold" style={{fontSize: '0.75rem'}}>{dup.activeClaims} Active</span> 
                                                                                      : <span className="text-muted" style={{fontSize: '0.75rem'}}>0 Active</span>}
                                                            </td>

                                                            {/* --- CONNECT IDs --- */}
                                                            <td>
                                                                <div className="mb-1">
                                                                    <Badge 
                                                                        bg={dup.connectAdjusterId ? "info" : "light"} 
                                                                        text={dup.connectAdjusterId ? "dark" : "muted"}
                                                                        className="fw-normal border d-flex align-items-center" 
                                                                        style={{fontSize: '0.7rem', width: 'fit-content'}}
                                                                    >
                                                                        <LuLink className="me-1" size={10}/> 
                                                                        Adj: {dup.connectAdjusterId || "None"}
                                                                    </Badge>
                                                                </div>
                                                                
                                                                <div>
                                                                    <Badge 
                                                                        bg={dup.connectAdminId ? "dark" : "light"}
                                                                        text={dup.connectAdminId ? "white" : "muted"} 
                                                                        className="fw-normal border d-flex align-items-center" 
                                                                        style={{fontSize: '0.7rem', width: 'fit-content'}}
                                                                    >
                                                                        <LuLink className="me-1" size={10}/> 
                                                                        Adm: {dup.connectAdminId || "None"}
                                                                    </Badge>
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <div className="text-truncate" style={{maxWidth: '180px'}} title={dup.email}>{dup.email}</div>
                                                                <div className="text-muted">{dup.phone}</div>
                                                            </td>

                                                            <td className="text-muted small">
                                                                <div className="text-truncate" style={{maxWidth: '200px'}} title={dup.address}>{dup.address}</div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={isContactMerging}>Close</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    contactDupes: state.contacts.contacts.contactDupes, 
    isContactDupesLoading: state.contacts.contacts.isContactDupesLoading,
    isContactMerging: state.contacts.contacts.isContactMerging,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {            
            getDupes: contactsActions.requestContactDupes,
            mergeContacts: contactsActions.requestContactMerge 
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(MergeModal);